import { Head, Link } from '@inertiajs/react'
import { withWorkspaceLayout } from '@/app/layouts/WorkspaceLayout'
import { type PageNode } from '@/widgets/sidebar/PageTree'

interface Workspace {
  id: number
  name: string
  slug: string
  role: string
}

interface ShowProps {
  workspace: Workspace
  pages: PageNode[]
  sidebar_pages: PageNode[]
  pending_change_requests_count?: number
}

function countPages(pages: PageNode[]): number {
  let count = 0
  for (const page of pages) {
    if (page.children.length > 0) {
      count += countPages(page.children)
    } else {
      count += 1
    }
  }
  return count
}

function Show({ workspace, pages, pending_change_requests_count }: ShowProps) {
  return (
    <>
      <Head title={workspace.name} />
      <div className="mx-auto max-w-4xl px-6 py-8">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">{workspace.name}</h2>
            <span className="mt-1 inline-block rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-600">
              {workspace.role}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {(workspace.role === 'admin') && (
              <>
                <Link
                  href={`/${workspace.slug}/import/new`}
                  className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Import ZIP
                </Link>
                <Link
                  href={`/${workspace.slug}/-/text-import`}
                  className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-700"
                >
                  Import text
                </Link>
                <form action={`/${workspace.slug}/export`} method="post" className="inline">
                  <input type="hidden" name="authenticity_token" value={document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || ''} />
                  <button
                    type="submit"
                    className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Export ZIP
                  </button>
                </form>
                <Link
                  href={`/${workspace.slug}/memberships`}
                  className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Team
                </Link>
                <Link
                  href={`/${workspace.slug}/change_requests`}
                  className="relative rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Change Requests
                  {!!pending_change_requests_count && (
                    <span className="absolute -right-1.5 -top-1.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1 text-xs font-medium text-white">
                      {pending_change_requests_count}
                    </span>
                  )}
                </Link>
              </>
            )}
          </div>
        </div>

        {pages.length === 0 ? (
          <div className="mt-12 text-center">
            <p className="text-lg text-gray-500">No pages yet.</p>
            <p className="mt-2 text-sm text-gray-400">
              Create your first page to get started.
            </p>
          </div>
        ) : (
          <div className="mt-8 text-center">
            <p className="text-4xl font-bold text-gray-900">{countPages(pages)}</p>
            <p className="mt-1 text-sm text-gray-500">pages in this workspace</p>
          </div>
        )}
      </div>
    </>
  )
}

Show.layout = withWorkspaceLayout
export default Show
